import { z } from "zod";
import type {
  BuildPipelineContext,
  ImageEditProvider,
  PipelineNode,
  PipelineSpec,
  Ref,
  ToolCore,
  ToolModelDef,
  VideoBgRemoveProvider,
  VideoProvider,
} from "@loumy/contracts";
import { modelEnum, resolveModelProvider, selectModel, withModelGating } from "@loumy/contracts";
import { CAP } from "@loumy/capabilities";

// §10.1 SpriteSheet-Generator (A, MVP — D48). DAG (§6):
//   input-validate(VLM-Gate, post-Hold) → input-normalize(cond verdict=edit, passThrough)
//   → prompt-enhance → video-gen(dur=Perioden-Match)
//   → loop=on + seamlessLoop=on (D131/D134/D159, Default — D137/D150): loop(TRIM-Modus: GPU
//     `loop-detect` erkennt das Cut-Fenster AUTOMATISCH (DINOv2-Embeddings, D134 — ersetzt
//     den manuellen D131-Pflicht-Trim)
//     → D159-Default (seamMode='vace'): GPU `loop-seam-vace` (EIN Op: Trim + VCU + self-hosted
//     Wan2.2-VACE-Fun-A14B-Inpaint + Splice) generiert die Naht-Brücke; Fallback seamMode='rife':
//     `loop-trim` (mode='rife') → frame-interpolation (RIFE via fal) → `frames-append` (D158;
//     der fal-VACE-Pfad bleibt als „D158-VACE"-Block deaktiviert)
//     → chroma-key(frames) → transparency-crop → spritesheet-pack
//   → loop=on + seamlessLoop=off (D137/D150): KEIN loop-Knoten — chroma-key(video→frames)
//     → transparency-crop → pack(dropLastFrame: das FLF-Naht-Duplikat fällt, D89-Mechanik);
//     der Loop bleibt die FLF-Transition (first==last) des gen-Knotens, etwas billiger
//     (der loop-Flat-4 entfällt, D158).
//   → loop=off: chroma-key(video→frames) → transparency-crop → [frame-interpolate?] → pack
//   outputs={ spritesheet ← pack.sheet, audio ← gen.audio (optional) }. Alle server (D45).
// D107: loop=on ist die FLF-Transition (first==last, D76) — periodische Bewegung + Rückkehr
//   zum Anker bleiben als Gen-Vorgabe (kleiner Rest-Gap am Schnitt ⇒ saubere Naht — D159-Default
//   generative VACE-Naht, RIFE-Fallback).
// D131 (User-Direktive): der Trim-Modus ersetzt in NEUEN Builds beide bisherigen
//   loop=on-Pfade — die D108/D113-Erkennung (loopFix + min-/maxLoopFrames, jetzt in den
//   Modellen versteckt) UND die D122-Drop-last+RIFE-Naht. Alt-Specs laufen über die
//   bestehenden loop-Capability-Zweige unverändert weiter (D32); der D130-Browser-Loop-Fix
//   bleibt als nachträglicher Edit am fertigen Sheet. D134: der manuelle Trim-Flow
//   (awaiting_trim/TrimEditor/Trim-Route) ist KOMPLETT entfernt — kein Job-Pausieren mehr.
// D77: frame-interpolate (Density-Anhebung) bleibt OPT-IN — Default ohne Warp-Blend.

// PLACEHOLDER (§13/D49). Deckt den realen Maximalfall fps 24 × Pixverse-maxDurationS 15 = 360
// Frames ab und lässt Headroom; deckelt absurde Frame-Zahlen (densify/refine).
// D109: exportiert — die ToolUI nutzt dieselben Bounds fürs maxLoopFrames-Feld (kein Drift).
export const MAX_FRAMES = 480;

// D95: Frames IMMER mit 24 fps (User-Spec) — KEIN Settings-Feld. Konstante an gen/loop/key/pack.
const FPS = 24;

// D110: Start der ähnlichste-Frame-Suche (loop-cut) — wieder USER-SETTING (das
// D108-Konstanten-Intermezzo entfiel auf User-Wunsch „ich sehe die MIN. frames nicht mehr",
// nachdem D109 das Max-Feld brachte). Historie: D95-Default 12 (die ersten Frames sind dem
// Start trivial ähnlich und würden den Loop verstümmeln; D94-Befund: 1s-Clip → Cut bei
// Frame 5 → 7-Frame-Sheet); seit 2026-06-10 Default 20 (User-Direktive nach den
// D166/D168-Live-Tests: längere Mindest-Fenster — ≥20 Frames ≈ ⅚ s bei 24 fps — verhindern
// Kurz-Zyklus-Fehlgriffe der DINO-Erkennung zuverlässiger; speist als detectMinFrames die
// Mindest-Fensterlänge von `loop-detect`, D134(i)). Exportiert als Schema-Default +
// UI-Startwert (D109/D110). Alte Specs unverändert (Literal eingefroren, D32/D34).
// D131: das Feld ist im loco-shift-Modell versteckt; Schema-Default/Export bleiben für
// Alt-Settings/D130-Browser-Loop-Fix erhalten.
export const MIN_LOOP_FRAMES = 20;

// D131: VACE-Naht-Parameter des Trim-Modus — als Literale in den loop-Knoten eingefroren
// (D34). contextFrames = echte Kontext-Frames je Seite der Naht (Identitäts-/Bewegungs-
// Anker fürs Inpainting); transitionFrames = GENERIERTE Übergangs-Frames, die Teil des
// Loops werden (Frame-Budget: Loop = Trim-Länge + transitionFrames). Das Wan-native
// 81-Frame-Konditions-Raster (Padding-Verwurf) baut der GPU-Op `loop-trim` (ops.py-SSOT).
// D158-VACE: diese vier Konstanten (TRIM_CONTEXT_FRAMES/TRIM_TRANSITION_FRAMES/
// SEAM_REPLACE_FRAMES/PAD_VACE_ASPECT) speisten die jetzt DEAKTIVIERTE VACE-Naht — der
// RIFE-Build (D158) nutzt sie NICHT mehr; erhalten als Doku/Resurrection-Anker (exportiert).
export const TRIM_CONTEXT_FRAMES = 12;
// (D135: nur noch Alt-Spec-/Append-Pfad — neue Builds senden seamReplaceFrames statt
// transitionFrames; der Capability-Default 8 bedient persistierte D131-Specs, D32.)
export const TRIM_TRANSITION_FRAMES = 8;

// D134: Mindest-Fensterlänge der automatischen Cut-Erkennung (`loop-detect`) — als Literal
// in den loop-Knoten eingefroren (D34-Geist: Heuristik-Input im Spec fixiert). Wert = der
// MIN_LOOP_FRAMES-Default (20 seit 2026-06-10, davor 12): kürzere Fenster wären kein sinnvoller Zyklus.
export const DETECT_MIN_FRAMES = MIN_LOOP_FRAMES;

// D135 (User-Direktive: „5 start+endframes nehmen und denoisen"): Replace-Fenster je Seite —
// VACE re-denoised die letzten r + ersten r Frames IN PLACE (Loop-Länge bleibt die
// natürliche Periode; die fixe 8-Frame-Einfügung entfällt). §13-kalibrierbar.
// D154: als fester loop-Knoten-Wert ABGELÖST durch den USER-Regler `seamVaceRadius`
// (0=Voll-Modus, ≥1=Replace-Fenster). Bleibt als dokumentierter Default-/Alt-Spec-Anker
// (der historische D135-Replace-Wert) erhalten.
export const SEAM_REPLACE_FRAMES = 5;

// D136: VACE-Kondition aufs nächstgelegene fal-Bucket (16:9|1:1|9:16) polstern + Padding
// nach der Generierung proportional zurückschneiden — das Endpoint kennt KEIN 3:4 und
// verzerrte sonst die Geometrie (Live-Befund: Naht-Frames ~4–6 % vertikal gestreckt).
export const PAD_VACE_ASPECT = true;

// Default-Qualitäts-Präferenz (order-unabhängig, D34) — erstes vom Provider unterstütztes
// gewinnt. 540p = Akquise/Qualitäts-Balance (§10.4). UI-Resolution-Wahl = §13-offen.
const RESOLUTION_PREFERENCE = ["540p", "720p", "360p", "1080p"];

// Perioden-Lookup-Tabelle (D49) — MVP-Konstante (§13). Start-Werte je Aktion;
// Skala = Auto-Periodenerkennung (Optical-Flow/Autokorrelation, §13).
export const PERIOD_LOOKUP: Record<string, number> = { walk: 1, run: 0.7, idle: 2, jump: 0.8 };

/** Loop-Periode der Aktion (D49 L1) — Default-Dauer ≈ Periode (sauberer one-shot-Loop). */
export function periodForAction(action?: string): number {
  return (action !== undefined ? PERIOD_LOOKUP[action] : undefined) ?? 1;
}

// D101: die Modell-Liste ist über einem expliziten TEIL-Feldtyp deklariert (nur die Felder,
// die Modelle anfassen) — bricht die Typ-Zirkularität models ↔ z.infer(Schema-Factory).
// D109–D113: loopFix/min-/maxLoopFrames dabei, weil loco-shift sie versteckt/strippt.
// D137/D150: seamlessLoop dito (loop-gebunden — loco-shift hat loop fest off).
// (D134: die D131-Felder trimStart/trimEnd sind mit dem manuellen Trim-Flow entfallen.)
type SpriteSheetModelFields = {
  loop: boolean;
  seamlessLoop?: boolean;
  seamFrames?: number; // D158/D159: RIFE-Brücken bzw. VACE-Replace-Fenster je Seite
  seamMode?: "vace" | "rife"; // D159: generative VACE-Naht (Default) vs RIFE-Fallback
  loopFix?: boolean;
  minLoopFrames?: number;
  maxLoopFrames?: number;
};

/**
 * D101: wählbare Modelle — reine Deklarationsdaten (§13-austauschbar), `models[0]` = Default.
 * v1 = Mechanismus mit dem bestehenden Pixverse-V6-Provider (D55/D57): 'Loco Shift' macht den
 * D91-Modus (gerichteter Übergang Start→Ende) als Modell wählbar — loop=off fest, loop-Feld
 * versteckt (der optionale end-frame-Input wird via visibleWhen sichtbar).
 * NAMEN (User-Entscheid): markeneigene Namen statt Original-Engine-Namen — die Engine
 * ist eine austauschbare Bindung (providerBindings/Slot-Default), kein Produktname.
 * D125: das D119-Zweitmodell „Loumy" (Kling O3 Standard, providerBindings { video: "fal-kling" })
 * wurde auf User-Direktive VOLLSTÄNDIG ENTFERNT — Kling O3 kombiniert FLF (end_image_url)
 * nicht mit multi_prompt („End Image Url is not supported with Multi Prompt"): 1s-Clips und
 * Loop-Garantie schlossen sich aus. Der 'video-gen#fal-kling'-Preis-Key bleibt als tote
 * Altlast im aktiven Book v4 (immutabel; loop-fix-Muster).
 */
export const SPRITESHEET_MODELS: readonly ToolModelDef<SpriteSheetModelFields>[] = [
  {
    id: "loco-motion",
    label: "LocoMotion",
    description: "Standard — Loop-Animation oder freier Clip.",
    // D131/D134: die ähnlichste-Frame-Erkennung (loopFix + Fenster-Felder) entfällt im
    // loop=on-Pfad — das Cut-Fenster erkennt seit D134 die GPU automatisch (`loop-detect`,
    // DINOv2); der D130-Browser-Loop-Fix bleibt als nachträglicher Edit.
    // D134(i) (User-Direktive nach Smoke #4 — „wackelt durchgehend", Detection wählte ein
    // 15-Frame-Fenster, davon 10 VACE-generiert): `minLoopFrames` ist WIEDER SICHTBAR und
    // speist als „Mind. Frames" die MINDEST-Fensterlänge der DINO-Erkennung (detectMinFrames).
    hiddenSettings: ["loopFix", "maxLoopFrames"],
  },
  {
    id: "loco-shift",
    label: "Loco Shift",
    description: "Gerichteter Übergang Start→Ende (optionalen End-Frame hochladen) — ohne Loop.",
    defaults: { loop: false },
    // D109–D113: loopFix/min-/maxLoopFrames mit-verstecken/-strippen — ohne Loop gibt es
    // keinen Cut, den der Schalter/die Fenster-Felder steuern könnten (loop ist hier fest off).
    // D137/D150: seamlessLoop dito (steuert NUR die loop=on-Naht-Stufe — hier wirkungslos).
    // D158: seamFrames dito (der RIFE-Naht-Regler greift nur im Trim-Modus; ersetzt seamVaceRadius).
    hiddenSettings: ["loop", "seamlessLoop", "seamFrames", "seamMode", "loopFix", "minLoopFrames", "maxLoopFrames"],
  },
];

/** Schema-Factory mit Provider-Bounds (D12/D44) — Submit-Validierung. */
export function makeSpriteSheetSettings(limits: { maxDurationS: number }) {
  return withModelGating(
    z
      .object({
      model: modelEnum(SPRITESHEET_MODELS), // D101: aus SPRITESHEET_MODELS abgeleitet (Single Source)
      prompt: z.string().min(1).max(2000),
      action: z.string().optional(), // wählt Perioden-Lookup → Dauer-Default (D49)
      // OPTIONAL (D49: Dauer ≈ Periode, NICHT statisch 2): fehlt der explizite Override,
      // leitet buildPipeline `durationS` aus periodForAction(action) ab. Ein gesetzter
      // Wert unterliegt den Provider-Bounds [1, maxDurationS].
      duration: z.number().min(1).max(limits.maxDurationS).optional(),
      loop: z.boolean().default(true),
      // D137/D150/D159 „Seamless Loop" (NUR bei loop=on wirksam; Default ON): ON = der D131/D134-
      // Trim-Modus (Auto-Cut via loop-detect + Naht — D159-Default: generative self-hosted VACE-Naht
      // `seamMode='vace'`, RIFE-Fallback; günstig: loop flat 4, kein Provider-Pin); OFF = der
      // einfache D89/D107-Pfad OHNE loop-Knoten —
      // gen(FLF first==last) → key → crop → pack(dropLastFrame: nur das Naht-Duplikat fällt).
      // Alte persistierte Settings ohne das Feld seedet der Default auf ON ⇒ Re-Submit baut
      // byte-identisch zum D136-Stand (D32). loco-shift versteckt/strippt das Feld (loop dort fest off).
      // D150: umbenannt von „extraImprovements" (Daten-Migration 0040 zieht Alt-Settings nach).
      seamlessLoop: z.boolean().default(true),
      // D158/D159: Naht-Stärke-Regler (nur im Trim-Modus wirksam: loop=on + seamlessLoop). Die
      // BEDEUTUNG hängt von `seamMode` ab (s. u.): im D159-Default 'vace' = Replace-Fenster JE
      // SEITE (D135, wie viele Frames die generative VACE-Naht neu rechnet); im 'rife'-Fallback =
      // Anzahl der interpolierten Brücken-Frames (D158). 1–8 (= frames-append-Cap, app.py); Default
      // 2. Ersetzt den D154-`seamVaceRadius` (alte Settings damit werden von Zod gestript, kein
      // Bruch wie einst `fps`/D95); persistierte NODE-Specs ohne seamFrames greifen den Capability-
      // Default 2 (D32). Historie: die D158-RIFE-Naht löste die fal-VACE-Sackgasse (NSFW-422/
      // Vollmasken-Halluzination) ab; D159 holt die generative VACE-Qualität SELF-HOSTED zurück.
      seamFrames: z.number().int().min(1).max(8).default(2),
      // D159: Naht-Verfahren. 'vace' (Default) = generative self-hosted Wan2.2-VACE-Fun-A14B-Naht
      // (MV2V-Inpaint auf Modal): generiert BEWEGTE Zwischenposen ⇒ kein RIFE-Stillstand am Naht-
      // Punkt; KEIN NSFW/Halluzination (self-hosted ⇒ kein fal-ref-Filter, schmale Maske = Anker).
      // seamFrames wirkt hier als Replace-Fenster JE SEITE (D135). 'rife' = D158-Fallback (fal-rife-
      // Interpolation, billiger/kein GPU, aber Stillstand-Risiko). Preis identisch (loop flat 4,
      // migrationsfrei: VACE-Pfad ohne Provider-Pin, RIFE-Pfad `loop#fal-rife`→`loop`). Alte
      // Settings ohne das Feld = Default 'vace' (Build setzt seamMode='vace'); persistierte NODE-
      // Specs ohne seamMode greifen den Capability-Default 'rife' (D32-resume-sicher, s. capabilities.ts).
      seamMode: z.enum(["vace", "rife"]).default("vace"),
      // D154-VACE-DEAKTIVIERT (D158-VACE): der frühere fal-VACE-Naht-Regler — nicht gelöscht,
      // dokumentiert den Voll-/Replace-Modus. Neue Submits nutzen seamFrames + seamMode (D159).
      // seamVaceRadius: z.number().int().min(0).max(16).default(0),
      // D146: bg-remove-Methode des generierten Videos — 'chroma' = Greenscreen-Keying (gratis,
      // Default); 'ai' = BEN2 AI-Matting (bezahlt, KEIN Greenscreen nötig, saubere Alpha-Kanten).
      // Ersetzt nur den key-Knoten (chroma-key → video-bg-remove); crop/pack/loop bleiben identisch.
      // (Der enhance-Greenscreen bleibt — BEN2 mattet die Vordergrund-Figur unabhängig von der
      // Hintergrundfarbe; so bleiben enhance/validate v1 unverändert.) Alte Settings ohne das
      // Feld = Default 'chroma' ⇒ Build byte-identisch (D32).
      bgRemove: z.enum(["chroma", "ai"]).default("chroma"),
      // D151 „Flüssige 24 FPS" (Default ON, KEINE Loop-Bindung — gilt für loop on/off und
      // loco-shift): Front-Knoten `frame-smooth` nach gen verwirft das Pixverse-„on twos"-
      // Doubling (konsekutive Beinahe-Duplikate, D128) und re-densifiziert via echtem RIFE
      // (fal-ai/rife, paarweise) zurück auf flüssige 24 fps; gibt ein VIDEO aus ⇒ loop/key
      // bleiben video-basiert. Ist nichts zu deduplizieren (3D-Look, 49/49 unique), reicht der
      // Knoten das Roh-Video durch (kein RIFE/COGS). Alte Settings ohne das Feld = Default ON;
      // OFF strippt den Knoten ⇒ Build byte-identisch zum Pre-D151-Stand (D32, presence-gated).
      smoothFrames: z.boolean().default(true),
      // D168 „Qualität verbessern" (Default OFF, bezahlt): optionaler SeedVR2-Pass nach
      // gen/smooth — temporal-konsistente Detail-/Schärfe-Rekonstruktion bei 2×-Auflösung
      // (Pixverse 540p → 1080p-Klasse; adressiert den „blurry"-Befund). Drop-in video→video
      // wie smooth; OFF/fehlend strippt den Knoten ⇒ Build byte-identisch (D32, presence-gated).
      enhanceQuality: z.boolean().default(false),
      audio: z.boolean().default(false),
      // D95: das frühere `fps`-Setting ist ENTFERNT (Frames IMMER 24 fps) — alte persistierte
      // Settings mit `fps` werden von Zod gestript (kein Submit-/Rehydrier-Bruch, wie loopFix/D94).
      // Frame-Density via frame-interpolate, entkoppelt von Loop-Dauer (D49). D77: OPT-IN —
      // ohne expliziten Wert läuft KEIN frame-interpolate (sonst Warp-Blend-Geister). D108:
      // wieder NUR bei loop=off wirksam (bei loop=on ist die vom Cut geschnittene Sequenz
      // final — ein Densify würde den Loop wieder aufblähen, D77/D94-Begründung).
      targetFrameCount: z.number().int().min(1).max(MAX_FRAMES).optional(),
      // D113 (User-Spec: „erkennung nur wenn Loop fix ON; loop on allein: letzten frame
      // entfernen"): loopFix wieder ein SETTING (wie einst D77, dort entfernt per D94) —
      // schaltet bei loop=on die ähnlichste-Frame-Erkennung (loop-Knoten) zu. Default off
      // (D122): loop=on allein = FLF-Transition + loop-Knoten im Drop-last+Naht-Modus
      // (Duplikat-Drop + 1 RIFE-Naht-Frame; ersetzt das frühere pack-dropLastFrame).
      // Alte D77-Settings mit loopFix wirken bei Re-Submit wieder (Semantik passt: loopFix
      // WAR der SSIM-nearest-Cut); D94-„gestript"-Ära beendet.
      loopFix: z.boolean().default(false),
      // D110 (User-Wunsch nach D109: „ich sehe die MIN. frames nicht mehr"): minLoopFrames
      // wieder USER-SETTING — Suchstart der ähnlichste-Frame-Erkennung (Mindest-Loop-Länge;
      // davor frühestens schneiden würde den Loop verstümmeln). Default = MIN_LOOP_FRAMES
      // (20 seit 2026-06-10, davor 12/D95/D108); persistierte Specs bleiben byte-identisch (D32).
      // D113: wirksam (wie maxLoopFrames) nur noch bei loop=on UND loopFix=on.
      // D134(i): ZUSÄTZLICH die Mindest-Fensterlänge der DINO-Cut-Erkennung im Trim-Pfad
      // (loop=on + seamlessLoop) — als „Mind. Frames" sichtbar; die Erkennung MUSS sie
      // einhalten (nur bei physisch zu kurzer Quelle klemmt der Op dokumentiert, ops.py).
      minLoopFrames: z.number().int().min(1).max(MAX_FRAMES).default(MIN_LOOP_FRAMES),
      // D109 (User-Spec): OPTIONALE Obergrenze der Loop-Länge in Frames — deckelt das
      // SUCHFENSTER der ähnlichste-Frame-Erkennung (loop-cut sucht dann in
      // [minLoopFrames, min(n, maxLoopFrames+1)) statt bis zum Sequenz-Ende ⇒ der
      // geschnittene Loop hat höchstens maxLoopFrames Frames). Nur bei loop=on wirksam
      // (der loop-Knoten existiert nur dort); Absenz = unbegrenzt (D108-Verhalten, D32).
      // D110: statische Untergrenze 1 — die echte Schranke ist das Cross-Field-Refine
      // unten (maxLoopFrames ≥ minLoopFrames; sonst wäre das Suchfenster leer).
      maxLoopFrames: z.number().int().min(1).max(MAX_FRAMES).optional(),
      // (Historie: D77-loopFix per D94 entfernt, per D107 bewusst nicht restauriert —
      // per D113 als Schalter ZURÜCK, per D131 in BEIDEN Modellen versteckt. Die
      // D131-Settings-Felder trimStart/trimEnd sind per D134 ENTFALLEN — das Cut-Fenster
      // erkennt die GPU automatisch; alte persistierte Settings mit den Feldern strippt
      // Zod beim Re-Submit, kein Rehydrier-Bruch.)
      // D76 „Trotzdem generieren": überspringt NUR die input-normalize-Edit-Schleife und füttert das
      // hochgeladene Bild DIREKT in die Video-Gen. Das input-validate-VLM-**Gate läuft trotzdem**
      // (Codex-P1.3, s. Build-Code: kein ungeprüfter reject/Policy-Bypass). Der User nutzt das nach
      // einem Validierungs-Fail mit dem bereits editierten prepared-input (oder einem neuen Bild).
      force: z.boolean().default(false),
    })
      // effektive Dauer (Override ODER Perioden-Default) begrenzt die Frame-Anzahl (D49).
      // D95: fps ist konstant 24 — die Schranke deckelt damit effektiv die Dauer auf 20 s.
      .refine(
        (s) => FPS * (s.duration ?? periodForAction(s.action)) <= MAX_FRAMES,
        "zu viele Frames (24 fps × effektive Dauer > MAX_FRAMES)",
      )
      // D110: Max ≥ Min — sonst wäre das loop-cut-Suchfenster [min, max+1) leer (app.py
      // lehnt das serverseitig ohnehin mit 400 ab; hier der definierte Submit-Fehler).
      // NUR prüfen, wenn der Cut auch läuft (Codex-P1 + D113: loop=on UND loopFix=on) — sonst
      // sind die Felder versteckt/wirkungslos und ein im State verbliebenes min>max dürfte
      // den Submit nicht unsichtbar blockieren (z. B. min=30/max=24 setzen, dann abschalten).
      .refine(
        (s) => !s.loop || !s.loopFix || s.maxLoopFrames === undefined || s.maxLoopFrames >= s.minLoopFrames,
        "Max. Frames muss ≥ Min. Frames sein",
      ),
    SPRITESHEET_MODELS, // D101: hidden strippen + Modell-Defaults seeden (ein Schema für Submit/Preview/UI)
  );
}

// Statisches Basis-Schema für den ToolCore (großzügige Bounds; Submit nutzt die Factory).
const StaticSpriteSheetSettings = makeSpriteSheetSettings({ maxDurationS: 60 });
export type SpriteSheetSettings = z.infer<typeof StaticSpriteSheetSettings>;

export const spriteSheetCore: ToolCore<SpriteSheetSettings> = {
  id: "spritesheet",
  category: "animation",
  // D91: optionaler `end-frame` (nur bei loop=off relevant) → gerichtete FLF-Transition
  // Start→Ende statt reinem i2v (Pixverse: /transition-Endpoint; Kling/D119: end_image_url).
  // Bei loop=on ignoriert (dort ist der End-Frame IMMER der Start-Frame selbst — FLF, D76/D107).
  inputs: [
    { kind: "image", min: 1, max: 1, role: "start-frame" },
    // D91: nur bei loop=off einblenden (gerichtete Transition); bei loop=on ignoriert der Build ihn ohnehin.
    { kind: "image", min: 0, max: 1, role: "end-frame", visibleWhen: { setting: "loop", equals: false } },
  ],
  settingsSchema: StaticSpriteSheetSettings,
  models: SPRITESHEET_MODELS,
  // Submit-Validierung (D44): provider-gebundene Bounds aus dem aufgelösten Video-Provider
  // (maxDurationS) — lehnt Werte ÜBER der Provider-Grenze ab, statt sie still zu
  // klemmen (buildPipeline klemmt nur den Default-Pfad als D34-Freeze, nicht User-Intent).
  // D95: maxFps entfällt als Bound (fps ist kein Setting mehr, konstant 24 ≤ Provider-maxFps 24).
  // D101 two-pass: Pass 1 liest NUR rawSettings.model (fehlt/unbekannt → Default-Modell) →
  // die Bounds spannen sich über den MODELL-gebundenen video-Provider (derselbe
  // resolveModelProvider wie im Build — gepreister Adapter == ausgeführter Adapter, D34).
  boundSettingsSchema(ctx: BuildPipelineContext, rawSettings: unknown) {
    const model = selectModel(SPRITESHEET_MODELS, rawSettings);
    const video = resolveModelProvider<VideoProvider>(ctx.providers, model, "video");
    return makeSpriteSheetSettings({ maxDurationS: video.limits.maxDurationS });
  },
  outputSlots: [
    { name: "spritesheet", kind: "spritesheet", optional: false },
    // Rohes Provider-Video (gen.video) UNBEARBEITET — vor loop/chroma-key/crop/interpolate/
    // pack (D68): als abspielbares + downloadbares Video. Diagnose (geht der gebundene
    // video-Adapter — Pixverse/Kling, D119? Greenscreen?) und legitimer Output für Nutzer,
    // die das Quell-Video wollen. fetchBytes-Kopie = 1:1.
    { name: "source-video", kind: "video", optional: true },
    // D76: das (ggf. editierte) Eingabebild, das in die Gen ging. Erhalten auch bei Validierungs-
    // Fail (prepared=false) → der User kann damit „Trotzdem generieren". Im force-Pfad nicht erzeugt.
    { name: "prepared-input", kind: "image", optional: true },
    { name: "audio", kind: "audio", optional: true },
  ],
  buildPipeline(settings, inputs, ctx: BuildPipelineContext): PipelineSpec {
    // Single-Pass (D34): Provider/Auflösung EINMAL aufgelöst & als Literale eingefroren.
    // D101: alle Slots über die MODELL-Bindung (providerBindings ODER Slot-Default; der
    // Bindings-Pfad wurde mit D119/Loumy erprobt und ist seit D125 wieder ungenutzt).
    // Derselbe Helper wie in boundSettingsSchema (Bounds == Build, D34).
    const model = selectModel(SPRITESHEET_MODELS, settings);
    const video = resolveModelProvider<VideoProvider>(ctx.providers, model, "video");
    // F-2 (D34/D56): den image-edit-Adapter des normalize-Knotens schon im Build auflösen &
    // pinnen (key-frei — der Adapter wirft erst beim echten edit()-Call). So laufen Pricing
    // (Build) und Ausführung (Laufzeit) garantiert auf demselben Adapter/derselben Version.
    const imageEdit = resolveModelProvider<ImageEditProvider>(ctx.providers, model, "image-edit");
    // Default-Qualität über eine STABILE Präferenzliste (nicht Array-Position), damit die
    // Reihenfolge der Provider-`resolutions` Preis/Qualität nicht still verschiebt (D34).
    // 540p = Balance aus Akquise-Preis & Qualität (§10.4); echte Resolution-Wahl = §13-offen.
    const resolution =
      RESOLUTION_PREFERENCE.find((r) => video.limits.resolutions.includes(r)) ??
      video.limits.resolutions[0] ??
      "720p";
    // Dauer = expliziter Override ODER Perioden-Match der Aktion (D49 L1). GANZZAHLIG
    // einfrieren: beide video-Adapter (Pixverse V6/D55, Kling O3/D119) generieren nur ganze Sekunden — die
    // geplante Dauer MUSS der ausgeführten entsprechen (D34), sonst driften Hold/Pricing
    // von den realen Kosten. Sub-sekündige Perioden (run 0.7) → 1 s (leichte Dauer-Rundung
    // nach oben — die FLF-Transition schließt den Loop unabhängig von der exakten Periode).
    // Fraktional-fähige Provider würden Granularität via limits annoncieren (§13).
    const durationS = Math.max(
      1,
      Math.min(video.limits.maxDurationS, Math.round(settings.duration ?? periodForAction(settings.action))),
    );
    const loop = settings.loop;
    // D137/D150/D159: Seamless Loop steuert bei loop=on die Naht-Stufe — nur loopTrim=true baut
    // den D131/D134-loop-Knoten (Auto-Cut + Naht: D159-Default generative VACE-Naht, RIFE-Fallback);
    // OFF bleibt die reine FLF-Transition, pack droppt das first==last-Naht-Duplikat (D89, s. Knoten 9).
    const loopTrim = loop && settings.seamlessLoop !== false;
    // D151 „Flüssige 24 FPS": Front-Knoten `frame-smooth` (Dedup + RIFE) — KEINE Loop-Bindung
    // (greift auch loop=off/loco-shift). Default ON; presence-gated (OFF = byte-identisch, D32).
    const smooth = settings.smoothFrames !== false;
    const action = settings.action;
    // D77/D108: Frame-Interpolate (Warp-Blend-Density-Anhebung) bleibt OPT-IN und ist wieder
    // NUR bei loop=off erlaubt — bei loop=on ist die vom Cut getrimmte Sequenz final (ein
    // Densify würde den gerade geschnittenen Loop wieder aufblähen, D77/D94-Begründung).
    const densify = settings.targetFrameCount !== undefined && !loop;
    const targetFrameCount = settings.targetFrameCount ?? 0;
    // D76 „Trotzdem generieren" (force): überspringt NUR die Edit-Schleife (normalize) und füttert
    // das hochgeladene Bild DIREKT in die Gen. Das VLM-**Gate (validate) läuft trotzdem** (Codex-P1.3):
    // sonst wäre `force:true` ein ungeprüfter Bypass des reject/Policy-Schutzes. force unterdrückt
    // nur das AUTO-Editieren (loop-Feasibility 'edit' wird ignoriert), nicht das Sicherheits-Gate.
    const force = settings.force === true;
    const startFrameRef: Ref = force
      ? { node: "$input", port: "start-frame" }
      : { node: "normalize", port: "image" };

    // D91: ein USER-End-Frame ist NUR bei loop=off relevant (bei loop=on ist der End-Frame
    // IMMER der Start-Frame selbst — FLF, D76/D107). Der Submit lässt für dieses Tool nur die
    // Rollen start-frame (genau 1) + end-frame (0–1) zu ⇒ inputs.length === 2 bedeutet
    // zuverlässig „End-Frame vorhanden" (AssetRef trägt keine Rolle; die $input-Ports lösen
    // zur Laufzeit rollen-basiert auf). Bei loop=off + End-Frame ⇒ gerichtete Transition.
    const hasEndFrame = !loop && inputs.length >= 2;

    const nodes: PipelineNode[] = [];

    // 1) VLM-Gate IMMER (post-Hold, D52/D76): reject → Job-Fail + release_hold (Runner). Der VLM matcht
    //    Input-Bild ↔ ROH-Prompt/Aktion (+ Loop-Feasibility bei loop=on) — als Literale eingefroren.
    nodes.push({
      id: "validate",
      capabilityId: CAP.inputValidate.id,
      capabilityVersion: CAP.inputValidate.version,
      surface: "server",
      inputs: { image: { node: "$input", port: "start-frame" } },
      params: { prompt: settings.prompt, loop, ...(action !== undefined ? { action } : {}) },
    });
    // 2) Edit↔Re-Validate-Schleife (cond verdict=edit, D76) — NUR ohne force: editiert das Bild
    //    prompt-/loop-treu (max 2 Runden) und re-validiert; prepared=false → Runner-Gate failt
    //    (prepared-input bleibt). Bei verdict=pass passThrough image→image (typ-rein, D30).
    if (!force) {
      nodes.push({
        id: "normalize",
        capabilityId: CAP.inputNormalize.id,
        capabilityVersion: CAP.inputNormalize.version,
        surface: "server",
        inputs: { image: { node: "$input", port: "start-frame" } },
        condition: { fromNode: "validate", signal: "verdict", op: "eq", value: "edit", onMissing: "skip" },
        passThrough: { image: "image" },
        // F-2 (D34/D56): image-edit-Adapter eingefroren (wie der gen-Knoten den Video-Adapter).
        params: {
          providerId: imageEdit.id,
          providerVersion: imageEdit.version,
          prompt: settings.prompt,
          loop,
          maxEdits: 2, // D76: max. 2 Edit-Runden (User-Entscheid)
          // D79: kontrastierende Screen-Farbe aus dem VLM-Urteil (validate.keyColor) — das editierte
          // Bild bekommt denselben Screen wie enhance fordert und key entfernt.
          keyColor: { from: "node", node: "validate", port: "keyColor" },
          // D114: der Grund des auslösenden Urteils → die ERSTE Edit-Runde fixt gezielt das
          // Beanstandete (vorher blind; erst Runde 2 kannte einen Re-Validate-Grund).
          reason: { from: "node", node: "validate", port: "reason" },
          ...(action !== undefined ? { action } : {}),
        },
      });
    }

    // 2b) D91 — gerichtete Transition (loop=off + End-Frame): den End-Frame SPIEGELBILDLICH zum
    //     Start behandeln — eigenes Content-Gate (validate-end, reject→Job-Fail wie der Start, der
    //     Runner-Gate ist capability-generisch) + screen-/minimaler Edit (normalize-end) mit
    //     DERSELBEN keyColor wie der Start (validate.keyColor) → EIN konsistenter Screen für den
    //     einen chroma-key. loop=false: KEIN Loop-Anker-Edit (der End-Frame ist eine freie Ziel-Pose,
    //     kein Ruhezustand). force überspringt — wie beim Start — nur den Edit, nicht das Gate.
    let endFrameRef: Ref | undefined;
    if (hasEndFrame) {
      const endInput: Ref = { node: "$input", port: "end-frame" };
      nodes.push({
        id: "validate-end",
        capabilityId: CAP.inputValidate.id,
        capabilityVersion: CAP.inputValidate.version,
        surface: "server",
        inputs: { image: endInput },
        params: { prompt: settings.prompt, loop: false, ...(action !== undefined ? { action } : {}) },
      });
      if (force) {
        endFrameRef = endInput;
      } else {
        nodes.push({
          id: "normalize-end",
          capabilityId: CAP.inputNormalize.id,
          capabilityVersion: CAP.inputNormalize.version,
          surface: "server",
          inputs: { image: endInput },
          condition: { fromNode: "validate-end", signal: "verdict", op: "eq", value: "edit", onMissing: "skip" },
          passThrough: { image: "image" },
          params: {
            providerId: imageEdit.id,
            providerVersion: imageEdit.version,
            prompt: settings.prompt,
            loop: false, // freie Ziel-Pose, KEIN Loop-Anker (D83/D84 betrifft nur den Loop-Startframe)
            maxEdits: 2,
            // SELBE Screen-Farbe wie der Start (validate.keyColor) → konsistenter Hintergrund für den
            // EINEN chroma-key (sonst grün↔blau-Mismatch zwischen Start- und End-Keyframe).
            keyColor: { from: "node", node: "validate", port: "keyColor" },
            // D114: Grund des End-Frame-Urteils (validate-end, nicht validate!) für die erste Runde.
            reason: { from: "node", node: "validate-end", port: "reason" },
            ...(action !== undefined ? { action } : {}),
          },
        });
        endFrameRef = { node: "normalize-end", port: "image" };
      }
    }

    // 3) Prompt veredeln (Prompt aus settings via ScalarRef, D41). mode='motion' (D68): der
    //    Enhancer schreibt einen I2V-BEWEGUNGS-Prompt + Screen (der video-Provider animiert das
    //    Bild → Video, nicht ein statisches Sheet). Die Aktion gibt dem LLM die Bewegung vor.
    //    D79: die Screen-FARBE kommt aus validate.keyColor (kontrastierend zum Motiv) → der
    //    Provider rendert green ODER blue.
    nodes.push({
      id: "enhance",
      capabilityId: CAP.promptEnhance.id,
      capabilityVersion: CAP.promptEnhance.version,
      surface: "server",
      // D127/D129: der ROHE Start-Frame als Motiv-Bild im HAUPT-Call (kein Extra-Titel-Call,
      // D129) — dient der Titel-Subjekt-Treue: der LLM sieht das Subjekt und benennt es in der
      // TITLE-Erstzeile, statt aus dem knappen Motion-Prompt eines zu raten (Live-Befund:
      // prompt „walking" ⇒ halluziniert „Dog Walking Service", für JEDEN Job gleich).
      // Bewusst $input statt normalize-Output: der Titel benennt den USER-Upload, und enhance
      // hängt nicht zusätzlich am konditionalen Edit-Knoten (der ohnehin nur den Screen ändert).
      inputs: { subject: { node: "$input", port: "start-frame" } },
      params: {
        prompt: { from: "settings", key: "prompt" },
        mode: "motion",
        loop, // bei loop=on fordert der Enhancer LINEARE Bewegung ohne Fade-in/-out (nahtloser Loop)
        keyColor: { from: "node", node: "validate", port: "keyColor" },
        // D119-Fix (Mechanik generisch, D125: kein aktueller Provider setzt das Limit): hartes
        // Prompt-Zeichenlimit des gebundenen Video-Endpoints — NUR bei Präsenz einfrieren
        // (Builds ohne Limit byte-identisch, D32); der Enhancer schreibt dann kompakt.
        ...(video.limits.maxPromptChars !== undefined ? { maxPromptChars: video.limits.maxPromptChars } : {}),
        ...(action !== undefined ? { action } : {}),
      },
    });
    // 4) Video-Generierung (Provider eingefroren; dur=Perioden-Match, D49). D107 (= D76-FLF
    //    restauriert): bei loop=on geht das (ggf. editierte) Start-Bild als Start- UND End-
    //    Frame in den Transition-Endpoint — first==last schließt den Loop by construction
    //    (das D83/D84-Normalize stellt sicher, dass die Pose ein loopbarer Ruhezustand ist).
    //    loop=off + End-Frame → gerichtete Transition Start→Ende (D91).
    nodes.push({
      id: "gen",
      capabilityId: CAP.videoGen.id,
      capabilityVersion: CAP.videoGen.version,
      surface: "server",
      // loop=on → first==last-Transition (endFrame = startFrame, D76/D107); loop=off +
      // End-Frame → gerichtete Transition Start→Ende (D91); loop=off ohne End-Frame → i2v.
      inputs: {
        startFrame: startFrameRef,
        ...(loop ? { endFrame: startFrameRef } : endFrameRef ? { endFrame: endFrameRef } : {}),
      },
      params: {
        prompt: { from: "node", node: "enhance", port: "prompt" },
        loop,
        durationS,
        audio: settings.audio,
        fps: FPS, // D95: IMMER 24 (kein Setting mehr)
        providerId: video.id,
        providerVersion: video.version,
        resolution,
      },
    });
    // 4b) D151 „Flüssige 24 FPS": Front-Knoten `frame-smooth` — verwirft das Pixverse-„on twos"-
    //    Doubling (Dedup naher Duplikat-Frames, D128) und re-densifiziert via echtem RIFE
    //    (fal-ai/rife, paarweise) zurück auf flüssige 24 fps. Gibt ein VIDEO aus (Drop-in für
    //    gen.video, User-Direktive „bei video lassen") ⇒ loop/key lesen `genVideoRef` statt fest
    //    gen.video und bleiben video-basiert. Läuft VOR loop-detect/key ⇒ die Loop-Erkennung
    //    sieht saubere 24-fps-Frames. Provider gepinnt (D34, Preis-Key 'frame-smooth#fal-rife').
    //    `source-video`-Output bleibt das ROHE gen-Video (D68, s. outputs). Bei nichts-zu-
    //    deduplizieren reicht die Capability das Roh-Video durch (kein RIFE/COGS).
    let genVideoRef: Ref = { node: "gen", port: "video" };
    if (smooth) {
      const rife = resolveModelProvider<{ id: string; version: number }>(ctx.providers, model, "frame-interpolation");
      nodes.push({
        id: "smooth",
        capabilityId: CAP.frameSmooth.id,
        capabilityVersion: CAP.frameSmooth.version,
        surface: "server",
        inputs: { video: genVideoRef },
        params: { fps: FPS, providerId: rife.id, providerVersion: rife.version },
      });
      genVideoRef = { node: "smooth", port: "video" };
    }
    // 4c) D168 „Qualität verbessern" (Default OFF, presence-gated/D32): nur der FAKTOR wird
    //    hier bestimmt — der Knoten selbst entsteht in 5b NACH der Naht (User-Direktive nach
    //    Live-Befund Job 2dbf3fbe: die VACE-Naht auf 2×-Material riss mit ~117 s/Step das
    //    900-s-ComfyUI-Timeout; außerdem schärft SeedVR2 so die Naht-Frames MIT — kein
    //    Weich-Saum). Codex-R2-P1 (Budget-Guard): der Pfad zwischen enhance und downscale
    //    (key/crop/R2-Frame-Persist) arbeitet auf dem hochskalierten Material — der Faktor
    //    ist AUFLÖSUNGSABHÄNGIG so gewählt, dass der SeedVR2-Output NIE die 1080p-Klasse
    //    (~2,1 MPx) übersteigt: die Größenordnung, die der 1080p-gen-Pfad OHNE enhance schon
    //    legitim durchläuft ⇒ RAM/Tick-Zeit/R2-Volumen bleiben in der bestehenden Klasse
    //    (deshalb bewusst KEIN neuer Pixel-Guard in r2.load_frames; §13). 1080p-Quellen bauen
    //    KEINEN Knoten: Material ist bereits die Ziel-Klasse, fal dokumentiert kein
    //    upscale_factor-Minimum (1.0-Restauration = unverifiziertes 422-Risiko) — der Toggle
    //    ist dort wirkungslos und kostet +0 cr (die Preview zeigt das; 1×-Restauration §13).
    //    Heute real immer 540p→2× (RESOLUTION_PREFERENCE); die Map deckt Provider-Drift ab.
    const enhanceFactor =
      settings.enhanceQuality === true
        ? ({ "360p": 2, "540p": 2, "720p": 1.5 } as Record<string, number | undefined>)[resolution]
        : undefined;
    // ── Post-Process-Kette (D77/D107/D131/D134/D137/D150/D151/D159) ──
    // loop=on + Seamless Loop (Default, D137/D150):
    //            gen(FLF) → loop(TRIM-Modus: Auto-Cut via `loop-detect` (DINOv2, D134)
    //            + Naht — D159-Default generative VACE-Naht `loop-seam-vace`, RIFE-Fallback)
    //            → key(frames) → crop → pack
    // loop=on ohne Seamless Loop (D137/D150): KEIN loop-Knoten —
    //            gen(FLF) → key(video) → crop → pack(dropLastFrame: Duplikat-Drop, D89)
    // loop=off : gen → key(video) → crop → [interpolate?] → pack
    // `framesRef` führt durch die Knoten, damit pack stets den letzten Knoten liest.
    // D131 (User-Direktive): der Trim-Modus ERSETZT in neuen Builds beide bisherigen
    // loop=on-Pfade (D108/D113-Erkennung UND D122-Drop-last+RIFE); die Naht erzeugt seit
    // D159 standardmäßig die generative self-hosted VACE-Naht (`seamMode='vace'`), mit der
    // D158-RIFE-Interpolation als `seamMode='rife'`-Fallback (der fal-VACE-Pfad bleibt als
    // D158-VACE-Block deaktiviert); loopFix/min-/
    // maxLoopFrames sind in den Modellen versteckt (Alt-Specs laufen über die bestehenden
    // loop-Zweige weiter, D32; der D130-Browser-Loop-Fix bleibt als nachträglicher Edit).
    // D134: die Cut-PHASE wählt nicht mehr der User (Klammern), sondern die automatische
    // DINOv2-Erkennung — kein Job-Pausieren mehr.

    // 5) D131/D134/D158 (loop=on + Seamless Loop, D137): Trim-Modus des loop-Knotens — OHNE
    //    trimStart/trimEnd (das Cut-Fenster erkennt die Capability via GPU `loop-detect`; bereits
    //    getrimmte D131-Alt-Specs mit Index-Params laufen byte-identisch, D32). detectMinFrames =
    //    User-Setting „Mind. Frames" (D134(i) — Default 20 seit 2026-06-10, davor 12; persistierte
    //    Specs byte-identisch, D32); seamFrames = User-Regler (Naht-Stärke, D159 mode-abhängig). D159-Default
    //    `seamMode='vace'` = generative self-hosted VACE-Naht (Modal-Op, KEIN Provider-Pin); der
    //    `seamMode='rife'`-Fallback nutzt den frame-interpolation-Pin (D158, wie jeder Provider-Pin
    //    im Build, D34). Preis-Key fällt in BEIDEN Fällen über die D101-Kette auf den Basis-`loop`
    //    (kein `loop#fal-vace`). Der auskommentierte fal-VACE-Pin/-Params s. D158-VACE unten.
    if (loopTrim) {
      // D159: seamMode='vace' (Default) = generative self-hosted VACE-Naht (Modal-Op
      // `loop-seam-vace`, KEIN Provider-Pin ⇒ loop-Preis-Key = Basis `loop` flat 4, migrationsfrei);
      // 'rife' = D158-Fallback (frame-interpolation-Pin). DINOv2-Cut (D134) bleibt in beiden Pfaden.
      const seamMode = settings.seamMode ?? "vace";
      if (seamMode === "vace") {
        nodes.push({
          id: "loop",
          capabilityId: CAP.loop.id,
          capabilityVersion: CAP.loop.version,
          surface: "server",
          inputs: { video: genVideoRef },
          params: {
            fps: FPS,
            trim: true,
            seamMode: "vace",
            detectMinFrames: settings.minLoopFrames ?? DETECT_MIN_FRAMES,
            // seamFrames = Replace-Fenster JE SEITE (D135), reused den User-Regler (1–8/Def 2).
            seamFrames: settings.seamFrames ?? 2,
            // D159 (Codex-R1-P2): der enhance-Motion-Prompt steuert die generative Naht (VACE
            // synthetisiert echte Bewegung ⇒ der Aktions-/Stil-Kontext zählt; ScalarRef wie der
            // D158-VACE-Block). Fehlt enhance (theoretisch), greift der generische Op-Default.
            prompt: { from: "node", node: "enhance", port: "prompt" },
          },
        });
      } else {
      // D158: die Loop-Naht entsteht durch RIFE-Interpolation (fal-rife) statt VACE-Generierung.
      // Der frame-interpolation-Provider wird gepinnt (Build==Laufzeit, D34); der loop-Preis-Key
      // fällt über die D101-Kette auf den Basis-`loop` (flat 4) zurück (kein video-inpaint-Pin
      // mehr ⇒ kein `loop#fal-vace` flat 25). Die DINOv2-Cut-Erkennung (loop-detect, D134) bleibt;
      // capabilities.ts sendet loop-trim `mode='rife'` (Trim + Boundary-Frames) und interpoliert
      // dann End→Start. VACE-Pin/-Params s. D158-VACE-Kommentar unten (nicht gelöscht).
      const seamInterp = resolveModelProvider<{ id: string; version: number }>(ctx.providers, model, "frame-interpolation");
      nodes.push({
        id: "loop",
        capabilityId: CAP.loop.id,
        capabilityVersion: CAP.loop.version,
        surface: "server",
        // D151: das (ggf. geglättete) gen-Video — `genVideoRef` zeigt auf smooth.video wenn
        // smoothFrames=on, sonst direkt auf gen.video (byte-identisch zum Pre-D151-Stand).
        inputs: { video: genVideoRef },
        params: {
          fps: FPS,
          trim: true,
          detectMinFrames: settings.minLoopFrames ?? DETECT_MIN_FRAMES,
          // D158: Anzahl RIFE-Brücken-Frames (User-Regler, ersetzt D154 seamVaceRadius).
          seamFrames: settings.seamFrames ?? 2,
          providerId: seamInterp.id,
          providerVersion: seamInterp.version,
          // === D158-VACE (DEAKTIVIERT, nicht gelöscht — grep „D158-VACE"): der VACE-Naht-Pin
          // + seine Node-Params. Neue Builds pinnen frame-interpolation (RIFE) statt video-inpaint.
          //   const seamInpainter = resolveModelProvider(ctx.providers, model, "video-inpaint");
          //   contextFrames: TRIM_CONTEXT_FRAMES,
          //   seamVaceRadius: settings.seamVaceRadius ?? 0,   // 0=Voll-Modus, ≥1=±r-Replace (D154)
          //   padVaceAspect: PAD_VACE_ASPECT,                 // D136 AR-Bucket-Padding
          //   prompt: { from: "node", node: "enhance", port: "prompt" },  // VACE-Inpaint-Prompt
          //   providerId: seamInpainter.id, providerVersion: seamInpainter.version,
          // === Ende D158-VACE ===
        },
      });
      }
    }

    // 5b) D168 „Qualität verbessern" — SeedVR2-Pass NACH der Naht (User-Direktive nach Live-
    //    Befund Job 2dbf3fbe): die VACE-Naht (5) rechnet so auf QUELLGRÖSSE (kein 2×-Timeout),
    //    und SeedVR2 schärft die generierten Naht-Frames MIT (einheitlicher Look, kein Weich-
    //    Saum). Trim-Pfad: frames vom loop-Knoten (die Capability assembled intern via GPU
    //    `frames-to-mp4`, D146-Muster); sonst: das (ggf. geglättete) gen-Video direkt. key
    //    liest das enhanced Video (video-Input — decode dort, wie im loop=off-Pfad heute).
    //    Provider gepinnt (D34, Preis-Key 'video-enhance#fal-seedvr' → D101 → 'video-enhance').
    let enhanceVideoRef: Ref | null = null;
    if (enhanceFactor !== undefined) {
      const seedvr = resolveModelProvider<{ id: string; version: number }>(ctx.providers, model, "video-enhance");
      nodes.push({
        id: "enhance-video",
        capabilityId: CAP.videoEnhance.id,
        capabilityVersion: CAP.videoEnhance.version,
        surface: "server",
        inputs: loopTrim ? { frames: { node: "loop", port: "frames" } } : { video: genVideoRef },
        params: { fps: FPS, upscaleFactor: enhanceFactor, providerId: seedvr.id, providerVersion: seedvr.version },
      });
      enhanceVideoRef = { node: "enhance-video", port: "video" };
    }

    // 6) Hintergrund entfernen → RGBA-Frames. enhance=on: das geschärfte 2×-Video (decode im
    //    key-Knoten, wie der loop=off-Pfad). Sonst Trim-Pfad (loopTrim): frames vom loop-Knoten
    //    (Decode lief dort); sonst (loop=off ODER Seamless Loop=off, D137): video vom gen-Knoten.
    //    Methode (D146): 'chroma' = Greenscreen-Keying (gratis) — keyColor = validate.keyColor
    //    (kontrastierend zum Motiv, D79); 'ai' = BEN2 AI-Matting (bezahlt, video-bg-remove-
    //    Capability, beidseitiger Input video|frames — Provider gepinnt, D34). Beide liefern
    //    RGBA-Frames am port 'frames' ⇒ crop/pack/interpolate stromabwärts identisch.
    const keyInputs: Record<string, Ref> = enhanceVideoRef
      ? { video: enhanceVideoRef }
      : loopTrim
        ? { frames: { node: "loop", port: "frames" } }
        : { video: genVideoRef }; // D151: smooth.video (wenn aktiv) statt fest gen.video
    if (settings.bgRemove === "ai") {
      const ben2 = resolveModelProvider<VideoBgRemoveProvider>(ctx.providers, model, "video-bg-remove");
      nodes.push({
        id: "key",
        capabilityId: CAP.videoBgRemove.id,
        capabilityVersion: CAP.videoBgRemove.version,
        surface: "server",
        inputs: keyInputs,
        params: { providerId: ben2.id, providerVersion: ben2.version, fps: FPS },
      });
    } else {
      nodes.push({
        id: "key",
        capabilityId: CAP.chromaKey.id,
        capabilityVersion: CAP.chromaKey.version,
        surface: "server",
        inputs: keyInputs,
        params: { fps: FPS, keyColor: { from: "node", node: "validate", port: "keyColor" } },
      });
    }
    let framesRef: Ref = { node: "key", port: "frames" };

    // 7) Transparenz-Crop (Union-BBox) — IMMER.
    nodes.push({
      id: "crop",
      capabilityId: CAP.transparencyCrop.id,
      capabilityVersion: CAP.transparencyCrop.version,
      surface: "server",
      inputs: { frames: framesRef },
    });
    framesRef = { node: "crop", port: "frames" };

    // 8) Frame-Density hoch (Warp-Blend → targetFrameCount, D49) — NUR bei explizitem
    //    targetFrameCount (densify opt-in, D77) und NUR bei loop=off (D108, s.o.).
    if (densify) {
      nodes.push({
        id: "interpolate",
        capabilityId: CAP.frameInterpolate.id,
        capabilityVersion: CAP.frameInterpolate.version,
        surface: "server",
        inputs: { frames: framesRef },
        params: { targetFrameCount },
      });
      framesRef = { node: "interpolate", port: "frames" };
    }

    // 8b) D168 Downscale-back (Codex-P1, „Supersampling"): enhanceQuality skalierte die
    //    Auflösung hoch (SeedVR2-Detail-Rekonstruktion) — die Pipeline lief intern auf dem
    //    hochskalierten Material (schärfere Kanten für Keying/Detect), und dieser Knoten
    //    bringt die Frames VOR dem Packen auf Quellgröße zurück (scale = 1/upscaleFactor):
    //    das Sheet bleibt byte-klassen-identisch zu heute (Browser-Edit-100-MPx-Budget/RAM/R2).
    //    ±1px-Rundungsrest bei krummen Provider-Maßen ist unkritisch — die finalen Sprite-
    //    Maße bestimmt ohnehin die crop-BBox (Codex-R2-Prüfpunkt 2). Knoten-Existenz ist an
    //    enhanceFactor gekoppelt (1080p baut weder enhance-video noch downscale, s. 4c).
    if (enhanceFactor !== undefined) {
      nodes.push({
        id: "downscale",
        capabilityId: CAP.framesResize.id,
        capabilityVersion: CAP.framesResize.version,
        surface: "server",
        inputs: { frames: framesRef },
        params: { scale: 1 / enhanceFactor },
      });
      framesRef = { node: "downscale", port: "frames" };
    }

    // 9) Packen → Spritesheet + Meta. D122: im Trim-Pfad KEIN pack-`dropLastFrame` — dort
    //    droppt der loop-Knoten (5) das FLF-Naht-Duplikat (Cut exklusiv bzw. drop-last);
    //    ein zusätzlicher pack-Drop würde einen ECHTEN Frame kosten. D137: bei loop=on OHNE
    //    Seamless Loop gibt es keinen loop-Knoten ⇒ pack droppt das Duplikat selbst
    //    (D89-Mechanik; der Capability-Param lebte für Alt-Specs durchgehend weiter, D32).
    //    Presence-gated: nur `true` wird gesendet (Builds ohne D137-OFF byte-identisch, D32).
    nodes.push({
      id: "pack",
      capabilityId: CAP.spritesheetPack.id,
      capabilityVersion: CAP.spritesheetPack.version,
      surface: "server",
      inputs: { frames: framesRef },
      params: { fps: FPS, loop, ...(loop && !loopTrim ? { dropLastFrame: true } : {}) },
    });

    const outputs: PipelineSpec["outputs"] = {
      spritesheet: { ref: { node: "pack", port: "sheet" } },
      // Roh-Video aus dem gen-Knoten (vor jeglichem Post-Process, D68).
      "source-video": { ref: { node: "gen", port: "video" } },
      audio: { ref: { node: "gen", port: "audio" }, optional: true },
    };
    // D76: das normalisierte/editierte Eingabebild als (optionaler) Output — erhalten auch bei
    // Validierungs-Fail (Runner materialisiert es vor dem Gate-Fail) → „Trotzdem generieren".
    // Im force-Pfad existiert kein normalize-Knoten ⇒ kein Binding.
    if (!force) outputs["prepared-input"] = { ref: { node: "normalize", port: "image" }, optional: true };

    return { nodes, outputs };
  },
};
